// src/components/CallbackHelp.tsx
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Phone } from "lucide-react";
import { API_BASE_URL } from "@/config";
import axios from "@/lib/axios";

interface CallbackHelpProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  /** Show the "OK" button that sends a callback request and closes (default: true) */
  showOk?: boolean;
  /** Show a "Call Us" button with a tel: link (default: false) */
  showCallUs?: boolean;
  /** Phone number used by the "Call Us" button (default: "7082937369") */
  phoneNumber?: string;
  /** Show a "WhatsApp Us" button (default: false) */
  showWhatsappUs?: boolean;
  /** WhatsApp number (default: "7082937369") */
  whatsappPhoneNumber?: string;
  /**
   * When true, clicking "Call Us" or "WhatsApp Us" also silently fires the
   * backend request_callback/ API in addition to its primary action (default: false)
   */
  silentlyTrigger?: boolean;
  /** Dialog title text (default: "Request Callback") */
  title?: string;
  /** Dialog description / body text (default: standard message) */
  description?: string;
  /**
   * When true, the dialog does NOT require an authenticated user.
   * Shows a phone number input + "Submit" button first; after a successful
   * backend submission the WhatsApp / Call Us buttons are revealed.
   * (default: false)
   */
  prelogin?: boolean;
}

export default function CallbackHelp({
  isOpen,
  setIsOpen,
  showOk = true,
  showCallUs = false,
  phoneNumber = "7082937369",
  showWhatsappUs = false,
  whatsappPhoneNumber = "7082937369",
  silentlyTrigger = false,
  title = "Request Callback",
  description = "Someone from the Intellend team will reach out to you shortly.",
  prelogin = false,
}: CallbackHelpProps) {
  const [pageUrl, setPageUrl] = useState("");

  // prelogin-specific state
  const [preloginPhone, setPreloginPhone] = useState("");
  const [preloginError, setPreloginError] = useState("");
  const [preloginSubmitting, setPreloginSubmitting] = useState(false);
  /** true once the phone has been successfully submitted to the backend */
  const [preloginSubmitted, setPreloginSubmitted] = useState(false);

  useEffect(() => {
    setPageUrl(window.location.href);
  }, []);

  // Reset prelogin state whenever the dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setPreloginPhone("");
      setPreloginError("");
      setPreloginSubmitting(false);
      setPreloginSubmitted(false);
    }
  }, [isOpen]);

  const triggerBackendCallback = async (extraData?: Record<string, unknown>) => {
    try {
      await axios.post(`${API_BASE_URL}/api/request_callback/`, {
        url: pageUrl,
        ...extraData,
      });
    } catch (error) {
      console.error("Failed to send callback request:", error);
    }
  };

  // ── Prelogin: submit phone number ──────────────────────────────────────────
  const handlePreloginSubmit = async () => {
    if (!/^\d{10}$/.test(preloginPhone.trim())) {
      setPreloginError("Please enter a valid 10-digit mobile number.");
      return;
    }
    setPreloginError("");
    setPreloginSubmitting(true);
    try {
      await triggerBackendCallback({
        prelogin: true,
        phone_number: preloginPhone.trim(),
      });
      setPreloginSubmitted(true);
    } finally {
      setPreloginSubmitting(false);
    }
  };

  // ── Authenticated OK ───────────────────────────────────────────────────────
  const handleOkClick = async () => {
    await triggerBackendCallback();
    setIsOpen(false);
  };

  // ── WhatsApp ───────────────────────────────────────────────────────────────
  const handleWhatsAppClick = () => {
    // In prelogin mode the backend was already called on Submit — don't double-send.
    if (silentlyTrigger && !preloginSubmitted) {
      if (prelogin && preloginPhone.trim()) {
        triggerBackendCallback({ prelogin: true, phone_number: preloginPhone.trim() });
      } else {
        triggerBackendCallback();
      }
    }
    const message = encodeURIComponent(`Hi, I'm stuck at ${pageUrl}. Please help.`);
    const whatsappUrl = `https://wa.me/91${whatsappPhoneNumber}?text=${message}`;
    window.open(whatsappUrl, "_blank");
    setIsOpen(false);
  };

  // ── Call Us ────────────────────────────────────────────────────────────────
  const handleCallClick = () => {
    // In prelogin mode the backend was already called on Submit — don't double-send.
    if (silentlyTrigger && !preloginSubmitted) {
      if (prelogin && preloginPhone.trim()) {
        triggerBackendCallback({ prelogin: true, phone_number: preloginPhone.trim() });
      } else {
        triggerBackendCallback();
      }
    }
    window.location.href = `tel:${phoneNumber}`;
    setIsOpen(false);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogContent className="max-w-xs mx-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-sm">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* ── Prelogin flow ─────────────────────────────────────────────── */}
        {prelogin && (
          <>
            {/* Phase 1: phone input + Submit */}
            {!preloginSubmitted && (
              <div className="flex flex-col gap-2 pb-1">
                <Input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Enter your 10-digit mobile number"
                  value={preloginPhone}
                  onChange={(e) => {
                    setPreloginPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                    setPreloginError("");
                  }}
                  className="w-full"
                  disabled={preloginSubmitting}
                />
                {preloginError && (
                  <p className="text-xs text-red-500">{preloginError}</p>
                )}
                <Button
                  onClick={handlePreloginSubmit}
                  disabled={preloginSubmitting}
                  className="bg-intellend-green hover:bg-intellend-green/90 w-full"
                >
                  {preloginSubmitting ? "Submitting…" : "Submit"}
                </Button>
                <AlertDialogCancel className="w-full">Cancel</AlertDialogCancel>
              </div>
            )}

            {/* Phase 2: WhatsApp / Call revealed after successful submission */}
            {preloginSubmitted && (
              <AlertDialogFooter className="flex flex-col gap-2">
                <p className="text-xs text-green-600 text-center">
                  ✓ Request received! You can also reach us directly:
                </p>

                {showWhatsappUs && (
                  <Button
                    onClick={handleWhatsAppClick}
                    className="bg-green-500 hover:bg-green-600 w-full flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp Us
                  </Button>
                )}

                {showCallUs && (
                  <Button
                    onClick={handleCallClick}
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <Phone className="w-4 h-4" />
                    Call Us
                  </Button>
                )}

                <AlertDialogCancel className="w-full">Close</AlertDialogCancel>
              </AlertDialogFooter>
            )}
          </>
        )}

        {/* ── Authenticated (non-prelogin) flow ─────────────────────────── */}
        {!prelogin && (
          <AlertDialogFooter className="flex flex-col gap-2">
            {showWhatsappUs && (
              <Button
                onClick={handleWhatsAppClick}
                className="bg-green-500 hover:bg-green-600 w-full flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp Us
              </Button>
            )}

            {showCallUs && (
              <Button
                onClick={handleCallClick}
                variant="outline"
                className="w-full flex items-center justify-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Phone className="w-4 h-4" />
                Call Us
              </Button>
            )}

            {showOk && (
              <AlertDialogAction
                onClick={handleOkClick}
                className="bg-intellend-green hover:bg-intellend-green/90 w-full"
              >
                OK
              </AlertDialogAction>
            )}

            {!showOk && (
              <AlertDialogCancel className="w-full">Cancel</AlertDialogCancel>
            )}
          </AlertDialogFooter>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
